import React, { useState, useEffect, useMemo } from 'react'
import { DEFAULT_TEMPLATE_VARIABLES } from './variables'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { Modal, Button, Badge, Alert } from 'react-bootstrap'

import { ChannelTabs } from './components/ChannelTabs'
import { VariantEditor } from './components/VariantEditor'
import { VariantPreview } from './components/VariantPreview'
import {
  GET_MESSAGE_TEMPLATE_WITH_VARIANTS,
} from '../../gql/queries/campaigns'
import {
  CREATE_CHANNEL_VARIANT,
  UPDATE_CHANNEL_VARIANT,
  DELETE_CHANNEL_VARIANT,
  TOGGLE_TEMPLATE_BLUEPRINT,
  DUPLICATE_MESSAGE_TEMPLATE,
} from '../../gql/mutations/campaigns'
import {
  CampaignChannel,
  ChannelVariant,
  ChannelVariantInput,
  MessageTemplate,
} from '../../types/campaigns'

import { SUPPORTED_CHANNELS } from '../../constants/campaigns'
import { useGetUser } from '../../store/userStore'
import { isAdministrator } from '../../helpers'
import { useIntl, FormattedMessage } from 'react-intl'
import {
  TEMPLATE_MESSAGES as M,
  ARROW_LEFT,
  ICON_EYE,
  ICON_VARIANTS,
  STAR_EMPTY,
  STAR_FILLED,
  withIcon,
} from './templateMessages'

const AVAILABLE_CHANNELS: CampaignChannel[] = SUPPORTED_CHANNELS

// Sample data for preview
const SAMPLE_VARIABLES = {
  firstName: 'Juan',
  lastName: 'Pérez',
  email: 'juan@example.com',
  phone: '+1234567890',
  dueDate: '2024-01-15',
  amount: '$150.00',
}

export const TemplateEditorPage: React.FC = () => {
  const intl = useIntl()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const permissions = useGetUser((state) => state.user?.permissions ?? [])
  const canManageBlueprints = isAdministrator(permissions)

  const [activeChannel, setActiveChannel] = useState<CampaignChannel>('EMAIL')
  const [currentVariant, setCurrentVariant] = useState<ChannelVariant | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [liveBodyText, setLiveBodyText] = useState('')
  const [liveMetadata, setLiveMetadata] = useState<any>(null)

  // Fetch template with variants
  const { data, loading, refetch } = useQuery(GET_MESSAGE_TEMPLATE_WITH_VARIANTS, {
    variables: { id },
    skip: !id,
  })

  const template: MessageTemplate | null = data?.messageTemplate || null

  // Determine visible channels based on metadata
  const visibleChannels = useMemo(() => {
    if (!template) return []

    // 1. Try metadata
    let meta = template.metadata
    if (typeof meta === 'string') {
      try { meta = JSON.parse(meta) } catch (e) { meta = {} }
    }

    const enabled = meta?.enabled_channels
    if (Array.isArray(enabled) && enabled.length > 0) {
      return AVAILABLE_CHANNELS.filter(ch => enabled.includes(ch))
    }

    // 2. Fallback to legacy channel
    if (template.channel && AVAILABLE_CHANNELS.includes(template.channel)) {
      return [template.channel]
    }

    // 3. Fallback to all (should rarely happen for created templates)
    return AVAILABLE_CHANNELS
  }, [template])

  // Set initial active channel when template loads
  useEffect(() => {
    if (visibleChannels.length > 0 && !visibleChannels.includes(activeChannel)) {
      setActiveChannel(visibleChannels[0])
    }
  }, [visibleChannels, activeChannel])


  // Mutations
  const [createVariant, { loading: creating }] = useMutation(CREATE_CHANNEL_VARIANT)
  const [updateVariant, { loading: updating }] = useMutation(UPDATE_CHANNEL_VARIANT)
  const [deleteVariant, { loading: deleting }] = useMutation(DELETE_CHANNEL_VARIANT)
  const [toggleBlueprintMutation, { loading: togglingBlueprint }] = useMutation(TOGGLE_TEMPLATE_BLUEPRINT)
  const [duplicateTemplateMutation, { loading: duplicatingTemplate }] = useMutation(DUPLICATE_MESSAGE_TEMPLATE)

  // Update current variant when channel changes
  useEffect(() => {
    if (template?.variants) {
      const variant = template.variants.find((v) => v.channel === activeChannel)
      setCurrentVariant(variant || null)
    }
  }, [activeChannel, template])

  // Reset live metadata when variant changes
  useEffect(() => {
    setLiveMetadata(null)
    setLiveBodyText('')
  }, [currentVariant])

  // Merge live metadata with current variant for preview
  const previewVariant = useMemo(() => {
    if (!currentVariant) return null

    // Ensure base metadata is an object
    let baseMeta: Record<string, any> = {}
    if (typeof currentVariant.metadata === 'string') {
      try { baseMeta = JSON.parse(currentVariant.metadata) } catch (e) { }
    } else {
      baseMeta = currentVariant.metadata || {}
    }

    return {
      ...currentVariant,
      metadata: liveMetadata
        ? { ...baseMeta, ...liveMetadata }
        : baseMeta
    }
  }, [currentVariant, liveMetadata])

  const handleSaveVariant = async (variantData: ChannelVariantInput) => {
    try {
      if (currentVariant) {
        // Update existing variant
        await updateVariant({
          variables: {
            id: currentVariant.id,
            ...variantData,
          },
        })
        toast.success('✅ Variante actualizada correctamente')
      } else {
        // Create new variant
        await createVariant({
          variables: {
            templateId: id,
            variantInput: variantData,
          },
        })
        toast.success('✅ Variante creada correctamente')
      }
      refetch()
    } catch (error: any) {
      toast.error(error.message || 'Error al guardar variante')
    }
  }

  const handleDeleteVariant = async () => {
    if (!currentVariant) return

    if (!window.confirm('¿Estás seguro de eliminar esta variante?')) return

    try {
      await deleteVariant({
        variables: { id: currentVariant.id },
      })
      toast.success('✅ Variante eliminada correctamente')
      refetch()
    } catch (error: any) {
      toast.error(error.message || 'Error al eliminar variante')
    }
  }

  const handleToggleBlueprint = async () => {
    if (!template) return
    if (!canManageBlueprints) {
      toast.error('Only administrators can change blueprint status')
      return
    }
    const newValue = !template.isBlueprint
    try {
      await toggleBlueprintMutation({
        variables: { id: template.id, isBlueprint: newValue },
      })
      toast.success(newValue ? '⭐ Marked as blueprint' : 'Blueprint status removed')
      refetch()
    } catch (error: any) {
      toast.error(error.message || 'Error toggling blueprint status')
    }
  }

  const handleDuplicateTemplate = async () => {
    if (!template) return

    try {
      const result = await duplicateTemplateMutation({
        variables: {
          id: template.id,
          newName: `${template.name} (Copy)`,
        },
      })

      const duplicatedId = result.data?.duplicateMessageTemplate?.template?.id
      toast.success('Template duplicated successfully')

      if (duplicatedId) {
        navigate(`/templates/edit/${duplicatedId}`)
        return
      }

      navigate('/templates')
    } catch (error: any) {
      toast.error(error.message || 'Could not duplicate template')
    }
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '400px' }}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>{intl.formatMessage(M.loading)}</span>
        </div>
      </div>
    )
  }

  if (!template) {
    return (
      <div className='alert alert-danger'>
        <FormattedMessage {...M.editorNotFound} /> <a href='/templates'><FormattedMessage {...M.editorBackToList} /></a>
      </div>
    )
  }

  const isReadOnlyBlueprint = template.isBlueprint && !canManageBlueprints

  return (
    <div className='container-fluid'>
      {/* Header */}
      <div className='d-flex justify-content-between align-items-center mb-5'>
        <div>
          <h1 className='mb-2'>
            {ICON_VARIANTS} {template?.name}
            {template?.isBlueprint && (
              <Badge bg='warning' text='dark' className='ms-2 fs-5'>
                {withIcon(STAR_FILLED, intl.formatMessage(M.blueprint))}
              </Badge>
            )}
          </h1>
          <p className='text-muted mb-0'>{template?.description}</p>
        </div>
        <div className='d-flex gap-2'>
          <button
            onClick={handleToggleBlueprint}
            className={`btn btn-sm ${template?.isBlueprint ? 'btn-warning' : 'btn-outline-secondary'}`}
            disabled={togglingBlueprint || !canManageBlueprints}
            title={intl.formatMessage(
              template?.isBlueprint ? M.unmarkBlueprint : M.markBlueprint
            )}
          >
            {template?.isBlueprint
              ? withIcon(STAR_FILLED, intl.formatMessage(M.blueprint))
              : withIcon(STAR_EMPTY, intl.formatMessage(M.editorMarkBlueprint))}
          </button>
          {isReadOnlyBlueprint && (
            <button
              onClick={handleDuplicateTemplate}
              className='btn btn-primary'
              disabled={duplicatingTemplate}
            >
              {intl.formatMessage(
                duplicatingTemplate ? M.editorDuplicating : M.editorDuplicateBlueprint
              )}
            </button>
          )}
          <button
            onClick={() => setShowPreview(!showPreview)}
            className='btn btn-light'
          >
            {showPreview
              ? withIcon(ICON_VARIANTS, intl.formatMessage(M.editorEdit))
              : withIcon(ICON_EYE, intl.formatMessage(M.editorPreview))}
          </button>
          <button onClick={() => navigate('/templates')} className='btn btn-secondary'>
            {withIcon(ARROW_LEFT, intl.formatMessage(M.back))}
          </button>
        </div>
      </div>

      {isReadOnlyBlueprint && (
        <Alert variant='info'>
          <FormattedMessage {...M.editorReadOnly} />
        </Alert>
      )}

      {/* Template Info */}
      <div className='card mb-5 shadow-sm'>
        <div className='card-body'>
          <div className='row'>
            <div className='col-md-3'>
              <small className='text-muted'><FormattedMessage {...M.type} /></small>
              <div className='fw-bold'>{template?.templateType}</div>
            </div>
            <div className='col-md-3'>
              <small className='text-muted'><FormattedMessage {...M.editorActiveChannels} /></small>
              <div className='fw-bold'>
                {visibleChannels.join(', ')}
              </div>
            </div>
            <div className='col-md-3'>
              <small className='text-muted'><FormattedMessage {...M.editorVariantsCreated} /></small>
              <div className='fw-bold'>
                <FormattedMessage
                  {...M.editorVariantsCount}
                  values={{created: template?.variants?.length || 0, total: visibleChannels.length}}
                />
              </div>
            </div>
            <div className='col-md-3'>
              <small className='text-muted'><FormattedMessage {...M.blueprint} /></small>
              <div className='fw-bold'>
                {template?.isBlueprint
                  ? <span className='text-warning'>{withIcon(STAR_FILLED, intl.formatMessage(M.editorBlueprintYes))}</span>
                  : <span className='text-muted'><FormattedMessage {...M.no} /></span>
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Channel Tabs */}
      <ChannelTabs
        channels={visibleChannels}
        activeChannel={activeChannel}
        onChannelChange={setActiveChannel}
        variants={template?.variants || []}
      />

      {/* Main Content: Editor */}
      <div className='row'>
        <div className='col-12'>
          {isReadOnlyBlueprint ? (
            <VariantPreview
              variant={
                previewVariant || currentVariant || {
                  id: 'readonly-preview',
                  channel: activeChannel,
                  subject: '',
                  bodyText: '',
                  bodyHtml: '',
                  whatsappTemplateId: '',
                  whatsappLanguage: 'es',
                  whatsappTemplateParams: [],
                  smsMaxLength: 160,
                  isDefault: false,
                  variables: [],
                  metadata: {},
                  createdAt: '',
                  updatedAt: '',
                }
              }
              variables={SAMPLE_VARIABLES}
              liveBodyText={liveBodyText}
            />
          ) : (
            <VariantEditor
              channel={activeChannel}
              variant={currentVariant}
              onSave={handleSaveVariant}
              onDelete={currentVariant ? handleDeleteVariant : undefined}
              templateVariables={
                Array.isArray(template.variables) && template.variables.length > 0
                  ? template.variables
                  : DEFAULT_TEMPLATE_VARIABLES
              }
              onBodyTextChange={setLiveBodyText}
              onMetadataChange={setLiveMetadata}
              canManageLockedBlocks={canManageBlueprints}
            />
          )}
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size={activeChannel === 'EMAIL' ? 'xl' : undefined}
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
              <FormattedMessage {...M.editorPreviewTitle} values={{channel: activeChannel}} />
            </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light p-0">
          <div className="d-flex justify-content-center py-4">
            {previewVariant ? (
              <VariantPreview
                variant={previewVariant}
                variables={SAMPLE_VARIABLES}
                liveBodyText={liveBodyText}
              />
            ) : (
              <div className='text-center py-5'>
                <h4 className='text-muted'><FormattedMessage {...M.editorNoVariant} /></h4>
                <p className='text-muted'><FormattedMessage {...M.editorNoVariantHint} /></p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
              <FormattedMessage {...M.close} />
            </Button>
        </Modal.Footer>
      </Modal>

      {/* Loading overlay */}
      {(creating || updating || deleting || duplicatingTemplate) && (
        <div
          className='position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center'
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}
        >
          <div className='card shadow'>
            <div className='card-body text-center p-5'>
              <div className='spinner-border text-primary mb-3' role='status'>
                <span className='visually-hidden'>{intl.formatMessage(M.saving)}</span>
              </div>
              <div><FormattedMessage {...M.editorSaving} /></div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
